import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RepeatIcon, SearchIcon, UserIcon, CreditCardIcon, ClockIcon, LinkIcon, MailIcon, PhoneIcon, CheckIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
interface RecurringPaymentProps {
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: string) => void;
  onReferenceChange: (reference: string) => void;
  onSubscriptionModeChange?: (mode: 'existing' | 'new') => void;
}
const customers = [{
  id: 'cust_1',
  name: 'John Doe',
  email: 'john@example.com',
  cards: [{
    id: 'card_1',
    last4: '4242',
    brand: 'Visa',
    expiry: '04/27'
  }, {
    id: 'card_2',
    last4: '5555',
    brand: 'Mastercard',
    expiry: '07/24',
    isExpired: true
  }, {
    id: 'card_3',
    last4: '9012',
    brand: 'Amex',
    expiry: '11/28'
  }]
}, {
  id: 'cust_2',
  name: 'Jane Smith',
  email: 'jane@example.com',
  cards: [{
    id: 'card_4',
    last4: '1234',
    brand: 'Visa',
    expiry: '09/26'
  }, {
    id: 'card_5',
    last4: '8877',
    brand: 'Mastercard',
    expiry: '03/28'
  }]
}];
const intervalOptions = [{
  value: 'weekly',
  label: 'Weekly',
  days: 7
}, {
  value: 'biweekly',
  label: 'Every 2 weeks',
  days: 14
}, {
  value: 'monthly',
  label: 'Monthly',
  days: 30
}, {
  value: 'quarterly',
  label: 'Quarterly',
  days: 90
}, {
  value: 'biannually',
  label: 'Every 6 months',
  days: 180
}, {
  value: 'annually',
  label: 'Annually',
  days: 365
}];
const cardBrandLogo = (brand: string) => {
  const {
    t
  } = useTranslation('payments');
  const b = brand.toLowerCase();
  if (b === 'visa') return <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/visa_logo.png" alt={t('components.CardOnFilePayment.attrs.visa')} className="h-4 object-contain" data-id="element-7665" />;
  if (b === 'mastercard') return <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/mastercard_logo.png" alt={t('components.CardOnFilePayment.attrs.mastercard')} className="h-6 object-contain" data-id="element-7666" />;
  if (b === 'amex') return <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/amex_logo.svg" alt={t('components.CardOnFilePayment.attrs.amex')} className="h-6 object-contain" data-id="element-7667" />;
  return <span className="text-[10px] font-bold uppercase text-gray-500" data-id="element-7668">
      {brand}
    </span>;
};
const formatDateShort = (d: Date) => d.toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});
const formatDateFull = (d: Date) => `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
function addInterval(date: Date, interval: string): Date {
  const d = new Date(date);
  switch (interval) {
    case 'weekly':
      d.setDate(d.getDate() + 7);
      break;
    case 'biweekly':
      d.setDate(d.getDate() + 14);
      break;
    case 'monthly':
      d.setMonth(d.getMonth() + 1);
      break;
    case 'quarterly':
      d.setMonth(d.getMonth() + 3);
      break;
    case 'biannually':
      d.setMonth(d.getMonth() + 6);
      break;
    case 'annually':
      d.setFullYear(d.getFullYear() + 1);
      break;
  }
  return d;
}
export function RecurringPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSubscriptionModeChange
}: RecurringPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [trialDays, setTrialDays] = useState('0');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<(typeof customers)[0] | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [subscriptionMode, setSubscriptionMode] = useState<'existing' | 'new'>('existing');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  // Interval dropdown
  const [intervalSearch, setIntervalSearch] = useState('');
  const [intervalOpen, setIntervalOpen] = useState(false);
  const intervalRef = useRef<HTMLDivElement>(null);
  // Date picker
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  useEffect(() => {
    onSubscriptionModeChange?.(subscriptionMode);
  }, [subscriptionMode, onSubscriptionModeChange]);
  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (intervalRef.current && !intervalRef.current.contains(e.target as Node)) setIntervalOpen(false);
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) setShowDatePicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/[^\d.]/g, '');
    setAmount(v);
    onAmountChange(v);
  };
  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReference(e.target.value);
    onReferenceChange(e.target.value);
  };
  const handleSelectCustomer = (customer: (typeof customers)[0]) => {
    setSelectedCustomer(customer);
    setSelectedCard(null);
    setSearchTerm('');
    const validCards = customer.cards.filter(c => !c.isExpired);
    if (validCards.length > 0) setSelectedCard(validCards[0].id);
  };
  const filteredCustomers = searchTerm ? customers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase())) : [];
  const filteredIntervals = intervalSearch ? intervalOptions.filter(o => o.label.toLowerCase().includes(intervalSearch.toLowerCase())) : intervalOptions;
  const selectedIntervalLabel = intervalOptions.find(o => o.value === billingInterval)?.label || '';
  // Calendar generation
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const calendarWeeks = useMemo(() => {
    const first = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const last = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
    const startDay = first.getDay();
    const daysInMonth = last.getDate();
    const weeks: (Date | null)[][] = [];
    let days: (Date | null)[] = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), i));
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) days.push(null);
        weeks.push(days);
        days = [];
      }
    }
    return weeks;
  }, [calendarMonth]);
  const isDatePast = (d: Date) => d < today;
  const isDateSelected = (d: Date) => startDate && d.getDate() === startDate.getDate() && d.getMonth() === startDate.getMonth() && d.getFullYear() === startDate.getFullYear();
  const isDateToday = (d: Date) => d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  // Generate billing cycle preview
  const billingPreview = useMemo(() => {
    const cycles: Array<{
      cycle: number;
      date: Date;
      amount: string;
      isTrial: boolean;
    }> = [];
    const effectiveStart = startDate || new Date();
    const trial = parseInt(trialDays) || 0;
    const trialEnd = new Date(effectiveStart);
    trialEnd.setDate(trialEnd.getDate() + trial);
    const maxCycles = billingCycles ? parseInt(billingCycles) : 12;
    const displayCycles = Math.min(maxCycles, 24);
    let currentDate = trial > 0 ? new Date(trialEnd) : new Date(effectiveStart);
    // If there's a trial, add trial entry
    if (trial > 0) {
      cycles.push({
        cycle: 0,
        date: new Date(effectiveStart),
        amount: '0',
        isTrial: true
      });
    }
    for (let i = 0; i < displayCycles; i++) {
      cycles.push({
        cycle: i + 1,
        date: new Date(currentDate),
        amount: amount || '0',
        isTrial: false
      });
      currentDate = addInterval(currentDate, billingInterval);
    }
    return cycles;
  }, [startDate, trialDays, billingCycles, billingInterval, amount]);
  return <div className="space-y-5" data-id="element-7669">
      {/* ─── SUBSCRIPTION TYPE ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-7670">
        <div className="p-6" data-id="element-7671">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-7672">
            {t('components.RecurringPayment.subscription_type')}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7673">
            <div className={`border rounded-lg p-4 cursor-pointer transition-all flex items-start gap-3 ${subscriptionMode === 'existing' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSubscriptionMode('existing')} data-id="element-7674">
              <input type="radio" name="subMode" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={subscriptionMode === 'existing'} onChange={() => setSubscriptionMode('existing')} data-id="element-7675" />

              <div data-id="element-7676">
                <div className="flex items-center gap-2" data-id="element-7677">
                  <CreditCardIcon size={15} className="text-[#9F140F]" data-id="element-7678" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-7679">
                    {t('components.RecurringPayment.existing_customer')}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-7680">
                  {t('components.RecurringPayment.charge_a_saved_payment_method_on')}
                </p>
              </div>
            </div>
            <div className={`border rounded-lg p-4 cursor-pointer transition-all flex items-start gap-3 ${subscriptionMode === 'new' ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSubscriptionMode('new')} data-id="element-7681">
              <input type="radio" name="subMode" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={subscriptionMode === 'new'} onChange={() => setSubscriptionMode('new')} data-id="element-7682" />

              <div data-id="element-7683">
                <div className="flex items-center gap-2" data-id="element-7684">
                  <LinkIcon size={15} className="text-[#9F140F]" data-id="element-7685" />
                  <p className="text-sm font-semibold text-gray-900" data-id="element-7686">
                    {t('components.RecurringPayment.new_subscription_link')}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-7687">
                  {t('components.RecurringPayment.send_a_link_to_a_customer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── CUSTOMER (EXISTING) ─── */}
      {subscriptionMode === 'existing' && <div className="bg-white rounded-xl border border-gray-200" data-id="element-7688">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-7689">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7690">
              <UserIcon size={16} className="mr-2 text-[#E2231A]" data-id="element-7691" />
              {t('components.BulkPaymentPreview.customer')}
            </h3>
          </div>

          {!selectedCustomer ? <div className="p-6" data-id="element-7692">
              <div className="relative" data-id="element-7693">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7694">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-7695" />
                </div>
                <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.RecurringPayment.attrs.search_by_name_or_email')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-7696" />

                {searchTerm && filteredCustomers.length > 0 && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-7697">
                    {filteredCustomers.map((customer, idx) => <button key={customer.id} type="button" onClick={() => handleSelectCustomer(customer)} className={`w-full px-4 py-3 text-left flex items-center hover:bg-gray-50 transition-colors ${idx < filteredCustomers.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-7698">
                        <div className="h-8 w-8 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-7699">
                          <UserIcon size={14} className="text-[#9F140F]" data-id="element-7700" />
                        </div>
                        <div data-id="element-7701">
                          <p className="text-sm font-medium text-gray-900" data-id="element-7702">
                            {customer.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-7703">
                            {customer.email}
                          </p>
                        </div>
                      </button>)}
                  </div>}
              </div>
            </div> : <>
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200" data-id="element-7704">
                <div className="flex items-center min-w-0" data-id="element-7705">
                  <div className="h-9 w-9 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-7706">
                    <UserIcon size={16} className="text-[#9F140F]" data-id="element-7707" />
                  </div>
                  <div className="min-w-0" data-id="element-7708">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-7709">
                      {selectedCustomer.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-7710">
                      {selectedCustomer.email}
                    </p>
                  </div>
                </div>
                <button onClick={() => {
            setSelectedCustomer(null);
            setSelectedCard(null);
          }} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors flex-shrink-0 ml-4" data-id="element-7711">
                  {t('components.CardOnFilePayment.change')}
                </button>
              </div>

              <div data-id="element-7712">
                {selectedCustomer.cards.map((card, idx) => {
            const expired = !!(card as any).isExpired;
            return <div key={card.id} className={`flex items-center px-6 py-4 transition-colors ${expired ? 'opacity-50 cursor-not-allowed bg-gray-50/50' : selectedCard === card.id ? 'bg-[#E2231A]/5 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} ${idx < selectedCustomer.cards.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => {
              if (!expired) setSelectedCard(card.id);
            }} data-id="element-7713">
                      <input type="radio" name="recurringCard" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={selectedCard === card.id} disabled={expired} onChange={() => {
                if (!expired) setSelectedCard(card.id);
              }} data-id="element-7714" />

                      <div className="ml-4 flex-1 flex items-center justify-between" data-id="element-7715">
                        <div className="flex items-center" data-id="element-7716">
                          <div className={`h-8 w-12 border rounded flex items-center justify-center mr-3 overflow-hidden ${expired ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'}`} data-id="element-7717">
                            {cardBrandLogo(card.brand)}
                          </div>
                          <div data-id="element-7718">
                            <p className={`text-sm font-medium ${expired ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-7719">
                              {card.brand} •••• {card.last4}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5" data-id="element-7720">
                              <p className={`text-xs ${expired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7721">
                                {t('components.CardOnFilePayment.expires')}{' '}
                                {card.expiry}
                              </p>
                              {expired && <span className="text-[10px] font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded" data-id="element-7722">
                                  {t('components.CardOnFilePayment.expired')}
                                </span>}
                            </div>
                          </div>
                        </div>
                        {!expired && selectedCard === card.id && <CheckIcon size={16} className="text-[#9F140F] flex-shrink-0" data-id="element-7723" />}
                      </div>
                    </div>;
          })}
              </div>
            </>}
        </div>}

      {/* ─── CUSTOMER INFO (NEW) ─── */}
      {subscriptionMode === 'new' && <div className="bg-white rounded-xl border border-gray-200" data-id="element-7724">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-7725">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7726">
              <UserIcon size={16} className="mr-2 text-[#E2231A]" data-id="element-7727" />
              {t('components.FixedLinksPayment.customer_information')}
            </h3>
            <p className="text-xs text-gray-500 mt-1" data-id="element-7728">
              {t('components.RecurringPayment.well_send_a_subscription_link_to')}
            </p>
          </div>
          <div className="p-6 space-y-4" data-id="element-7729">
            <div data-id="element-7730">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7731">
                {t('components.FixedLinksPayment.name')}{' '}
                <span className="normal-case text-gray-400" data-id="element-7732">
                  {t('components.RecurringPayment.optional')}
                </span>
              </label>
              <div className="relative" data-id="element-7733">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7734">
                  <UserIcon size={14} className="text-gray-400" data-id="element-7735" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder={t('components.RecurringPayment.attrs.john_doe')} value={customerName} onChange={e => setCustomerName(e.target.value)} data-id="element-7736" />
              </div>
            </div>
            <div data-id="element-7737">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7738">
                {t('components.RecurringPayment.email')}{' '}
                <span className="text-red-400" data-id="element-7739">*</span>
              </label>
              <div className="relative" data-id="element-7740">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7741">
                  <MailIcon size={14} className="text-gray-400" data-id="element-7742" />
                </div>
                <input type="email" className={`${inputClass} pl-9`} placeholder={t('components.RecurringPayment.attrs.customerexamplecom')} value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} data-id="element-7743" />
              </div>
            </div>
            <div data-id="element-7744">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7745">
                {t('components.RecurringPayment.phone')}{' '}
                <span className="normal-case text-gray-400" data-id="element-7746">
                  {t('components.RecurringPayment.optional')}
                </span>
              </label>
              <div className="relative" data-id="element-7747">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7748">
                  <PhoneIcon size={14} className="text-gray-400" data-id="element-7749" />
                </div>
                <input type="tel" className={`${inputClass} pl-9`} placeholder="+66 81 234 5678" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} data-id="element-7750" />
              </div>
            </div>
          </div>
        </div>}

      {/* ─── SUBSCRIPTION DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-7751">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7752">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7753">
            <RepeatIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7754" />
            {t('components.RecurringPayment.subscription_details')}
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-7755">
          <div data-id="element-7756">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7757">
              {t('components.RecurringPayment.amount_per_billing_period')}{' '}
              <span className="text-red-400" data-id="element-7758">*</span>
            </label>
            <div className="relative" data-id="element-7759">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7760">
                <span className="text-sm font-medium text-gray-500" data-id="element-7761">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={handleAmountChange} data-id="element-7762" />
            </div>
          </div>
          <div data-id="element-7763">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7764">
              {t('components.BulkPaymentPreview.description')}{' '}
              <span className="text-red-400" data-id="element-7765">*</span>
            </label>
            <input type="text" className={inputClass} placeholder={t('components.RecurringPayment.attrs.eg_premium_membership')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-7766" />
          </div>
          <div data-id="element-7767">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7768">
              {t('components.BulkPaymentPreview.reference')}{' '}
              <span className="normal-case text-gray-400" data-id="element-7769">
                {t('components.RecurringPayment.optional')}
              </span>
            </label>
            <input type="text" className={inputClass} placeholder={t('components.RecurringPayment.attrs.internal_reference')} value={reference} onChange={handleReferenceChange} data-id="element-7770" />
          </div>
        </div>
      </div>

      {/* ─── BILLING SCHEDULE ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-7771">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7772">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7773">
            <ClockIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7774" />
            {t('components.RecurringPayment.billing_schedule')}
          </h3>
        </div>
        <div className="p-6 space-y-4" data-id="element-7775">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7776">
            {/* Searchable interval dropdown */}
            <div ref={intervalRef} className="relative" data-id="element-7777">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7778">
                {t('components.RecurringPayment.billing_interval')}
              </label>
              <button type="button" aria-expanded={intervalOpen} aria-haspopup="listbox" className={`${inputClass} text-left flex items-center justify-between cursor-pointer`} onClick={() => {
              setIntervalOpen(!intervalOpen);
              setIntervalSearch('');
            }} data-id="element-7779">
                <span className={selectedIntervalLabel ? 'text-gray-900' : 'text-gray-400'} data-id="element-7780">
                  {selectedIntervalLabel || 'Select interval'}
                </span>
                <ChevronDownIcon size={14} className={`text-gray-400 transition-transform ${intervalOpen ? 'rotate-180' : ''}`} data-id="element-7781" />
              </button>

              {intervalOpen && <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-7782">
                  <div className="p-2 border-b border-gray-100" data-id="element-7783">
                    <div className="relative" data-id="element-7784">
                      <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-7785" />

                      <input type="text" className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] placeholder:text-gray-400" placeholder={t('components.RecurringPayment.attrs.search')} value={intervalSearch} onChange={e => setIntervalSearch(e.target.value)} autoFocus data-id="element-7786" />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto" data-id="element-7787">
                    {filteredIntervals.length > 0 ? filteredIntervals.map(opt => <button key={opt.value} type="button" className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${billingInterval === opt.value ? 'bg-[#E2231A]/5 text-[#E2231A] font-medium' : 'text-gray-700'}`} onClick={() => {
                  setBillingInterval(opt.value);
                  setIntervalOpen(false);
                }} data-id="element-7788">
                          {opt.label}
                          {billingInterval === opt.value && <CheckIcon size={14} className="text-[#9F140F]" data-id="element-7789" />}
                        </button>) : <div className="px-3 py-4 text-sm text-gray-500 text-center" data-id="element-7790">
                        {t('components.RecurringPayment.no_results')}
                      </div>}
                  </div>
                </div>}
            </div>

            <div data-id="element-7791">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7792">
                {t('components.RecurringPayment.billing_cycles')}{' '}
                <span className="normal-case text-gray-400" data-id="element-7793">
                  {t('components.RecurringPayment.optional')}
                </span>
              </label>
              <input type="number" min="1" className={inputClass} placeholder={t('components.RecurringPayment.attrs.unlimited')} value={billingCycles} onChange={e => setBillingCycles(e.target.value)} data-id="element-7794" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7795">
            {/* Date picker */}
            <div ref={datePickerRef} className="relative" data-id="element-7796">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7797">
                {t('components.RecurringPayment.start_date')}{' '}
                <span className="normal-case text-gray-400" data-id="element-7798">
                  {t('components.RecurringPayment.optional')}
                </span>
              </label>
              <button type="button" aria-expanded={showDatePicker} aria-haspopup="dialog" className={`${inputClass} text-left flex items-center justify-between cursor-pointer`} onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-7799">
                <div className="flex items-center gap-2" data-id="element-7800">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-7801" />
                  <span className={startDate ? 'text-gray-900' : 'text-gray-400'} data-id="element-7802">
                    {startDate ? formatDateFull(startDate) : 'Start immediately'}
                  </span>
                </div>
                <ChevronDownIcon size={14} className={`text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} data-id="element-7803" />
              </button>

              {showDatePicker && <div className="absolute z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-80 left-0" data-id="element-7804">
                  <div className="flex justify-between items-center mb-3" data-id="element-7805">
                    <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} data-id="element-7806">
                      <ChevronLeftIcon size={14} data-id="element-7807" />
                    </button>
                    <div className="text-sm font-semibold text-gray-900" data-id="element-7808">
                      {calendarMonth.toLocaleString('default', {
                    month: 'long',
                    year: 'numeric'
                  })}
                    </div>
                    <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} data-id="element-7809">
                      <ChevronRightIcon size={14} data-id="element-7810" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-400 mb-1" data-id="element-7811">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} data-id="element-7812">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center" data-id="element-7813">
                    {calendarWeeks.flatMap((week, wi) => week.map((date, di) => date ? <button key={`${wi}-${di}`} type="button" disabled={isDatePast(date)} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-colors ${isDateSelected(date) ? 'bg-[#E2231A] text-white' : isDateToday(date) ? 'border border-[#E2231A] text-[#E2231A]' : isDatePast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => {
                  setStartDate(date);
                  setShowDatePicker(false);
                }} data-id="element-7814">
                            {date.getDate()}
                          </button> : <div key={`${wi}-${di}`} className="w-8 h-8" data-id="element-7815" />))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2" data-id="element-7816">
                    <button type="button" className="flex-1 text-xs font-medium text-[#E2231A] hover:bg-[#E2231A]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                  setStartDate(null);
                  setShowDatePicker(false);
                }} data-id="element-7817">
                      {t('components.RecurringPayment.start_immediately')}
                    </button>
                    <button type="button" className="flex-1 text-xs font-medium text-[#E2231A] hover:bg-[#E2231A]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                  setStartDate(new Date());
                  setShowDatePicker(false);
                }} data-id="element-7818">
                      {t('components.InstallmentPayment.today')}
                    </button>
                  </div>
                </div>}
            </div>

            <div data-id="element-7819">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7820">
                {t('components.RecurringPayment.trial_period_days')}
              </label>
              <input type="number" min="0" className={inputClass} placeholder="0" value={trialDays} onChange={e => setTrialDays(e.target.value)} data-id="element-7821" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── BILLING CYCLE PREVIEW ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7822">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-7823">
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-7824">
            {t('components.RecurringPayment.billing_cycle_preview')}
          </h3>
          <span className="text-xs text-gray-400" data-id="element-7825">
            {billingCycles ? `${billingCycles} cycles` : 'Ongoing'} ·{' '}
            {selectedIntervalLabel}
          </span>
        </div>

        {billingPreview.length > 0 ? <div className="max-h-80 overflow-y-auto" data-id="element-7826">
            {billingPreview.map((cycle, idx) => <div key={idx} className={`flex items-center justify-between px-6 py-3 ${idx < billingPreview.length - 1 ? 'border-b border-gray-200' : ''}`} data-id="element-7827">
                <div className="flex items-center gap-3" data-id="element-7828">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${cycle.isTrial ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-7829">
                    {cycle.isTrial ? 'T' : cycle.cycle}
                  </div>
                  <div data-id="element-7830">
                    <p className="text-sm text-gray-900" data-id="element-7831">
                      {formatDateShort(cycle.date)}
                    </p>
                    {cycle.isTrial && <p className="text-[11px] text-blue-500" data-id="element-7832">
                        {t('components.RecurringPayment.trial_period')}{' '}
                        {trialDays} {t('components.RecurringPayment.days_free')}
                      </p>}
                  </div>
                </div>
                <span className={`text-sm font-medium ${cycle.isTrial ? 'text-blue-500' : 'text-gray-900'}`} data-id="element-7833">
                  {cycle.isTrial ? `${getTenant().currency.code} 0` : amount ? `${getTenant().currency.code} ${amount}` : '—'}
                </span>
              </div>)}
            {!billingCycles && <div className="px-6 py-3 border-t border-gray-200 bg-gray-50/50 text-center" data-id="element-7834">
                <p className="text-xs text-gray-400" data-id="element-7835">
                  {t('components.RecurringPayment.continues_indefinitely')}
                </p>
              </div>}
          </div> : <div className="px-6 py-8 text-center" data-id="element-7836">
            <p className="text-sm text-gray-500" data-id="element-7837">
              {t('components.RecurringPayment.enter_an_amount_and_billing_interval')}
            </p>
          </div>}
      </div>
    </div>;
}